import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { NewBadge } from "design-system/molecules/NewBadge"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { FieldValues, Path, UseFormReturn } from "react-hook-form"
import { Loader2, WandSparkles } from "lucide-react"
import { useTranslation } from "react-i18next"
import { GeneratedRequirement, InstructionCapabilityConfigSchema } from "schema"

type VariableOption = {
  value: string
  label: string
}

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>
  showCache?: boolean
  translationPrefix?: string
  variables?: VariableOption[]
  requirementsExtraction?: {
    requirements: GeneratedRequirement[]
    onExtract: () => void
    isPending: boolean
    disabled: boolean
  }
}

export function AgentAccordion<T extends FieldValues>({
  form,
  showCache = false,
  translationPrefix = "webhookConfig",
  variables = [],
  requirementsExtraction,
}: Props<T>) {
  const { t } = useTranslation()

  return (
    <Accordion
      type="single"
      collapsible
      className={cn(
        "border rounded-lg bg-secondary",
        form.formState.errors.agentPrompt && "border-destructive",
      )}
    >
      <AccordionItem value="agent">
        <AccordionTrigger className="text-lg">
          {t(`capabilities.${translationPrefix}.agent`)}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name={"agentPrompt" as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(`capabilities.${translationPrefix}.agentPrompt.label`)}</FormLabel>
                <FormDescription className="text-sm text-muted-foreground">
                  {t(`capabilities.${translationPrefix}.agentPrompt.description`)}
                </FormDescription>
                <FormControl>
                  <VaultVariableAutocomplete variables={variables}>
                    <Textarea
                      placeholder={t(`capabilities.${translationPrefix}.agentPrompt.placeholder`)}
                      rows={4}
                      maxLength={
                        InstructionCapabilityConfigSchema.shape.agentPrompt.maxLength ?? undefined
                      }
                      className="min-h-32"
                      {...field}
                    />
                  </VaultVariableAutocomplete>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {requirementsExtraction && (
            <AdminOnly>
              <div className="flex flex-col gap-3 rounded-md border bg-background p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {t("capabilities.instructionConfig.requirements.label")}
                      </p>
                      <NewBadge label={t("shared.beta")} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("capabilities.instructionConfig.requirements.description")}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={requirementsExtraction.onExtract}
                    disabled={requirementsExtraction.disabled || requirementsExtraction.isPending}
                  >
                    {requirementsExtraction.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <WandSparkles className="size-4" />
                    )}
                    {requirementsExtraction.isPending
                      ? t("capabilities.instructionConfig.requirements.extracting")
                      : t("capabilities.instructionConfig.requirements.extract")}
                  </Button>
                </div>
                {requirementsExtraction.requirements.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {requirementsExtraction.requirements.map((requirement, index) => (
                      <div
                        key={`${requirement.rule}-${index}`}
                        className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 p-3"
                      >
                        <Badge variant="outline">{requirement.rule}</Badge>
                        <p className="text-sm">{requirement.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t("capabilities.instructionConfig.requirements.empty")}
                  </p>
                )}
              </div>
            </AdminOnly>
          )}
          {showCache && (
            <FormField
              control={form.control}
              name={"useCache" as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center justify-between gap-4 py-1">
                      <Label htmlFor="use-cache-switch" className="flex flex-col gap-0.5">
                        <span>{t(`capabilities.${translationPrefix}.useCache.label`)}</span>
                        <span className="text-xs text-muted-foreground font-normal">
                          {t(`capabilities.${translationPrefix}.useCache.description`)}
                        </span>
                      </Label>
                      <Switch
                        id="use-cache-switch"
                        value={field.value?.toString()}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name={"executeOnlyOnce" as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex items-center justify-between gap-4 py-1">
                    <Label htmlFor="execute-only-once-switch" className="flex flex-col gap-0.5">
                      <span>{t(`capabilities.${translationPrefix}.executeOnlyOnce.label`)}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {t(`capabilities.${translationPrefix}.executeOnlyOnce.description`)}
                      </span>
                    </Label>
                    <Switch
                      id="execute-only-once-switch"
                      value={field.value?.toString()}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
